import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import api from '../api';
import {
  KeyRound, Mail, ArrowLeft, Info, CheckCircle2, Send, Clock
} from 'lucide-react';
import useNoIndex from '../hooks/useNoIndex';

/**
 * Mot de passe oublié :
 * l'utilisateur saisit son email → une demande est créée en base et apparaît
 * dans le back-office admin avec un badge. L'admin le contactera avec un
 * nouveau mot de passe temporaire sous 24 h.
 *
 * Quand Resend sera activé (Phase 2), ce flow sera automatisé par email.
 */
export default function ForgotPassword() {
  useNoIndex();
  const { dark } = useApp();
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.submitPasswordResetRequest(email.trim(), message.trim());
      setSubmitted(true);
      toast.success(res.message || 'Demande envoyée');
    } catch (err) {
      toast.error(err.message || 'Erreur lors de l\'envoi');
    } finally {
      setLoading(false);
    }
  };

  // --- Écran de confirmation ---
  if (submitted) {
    return (
      <div className="flex items-center justify-center py-8" data-testid="forgot-password-success">
        <div className="mc-card p-6 sm:p-8 w-full max-w-md text-center">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-emerald-100 dark:bg-emerald-900/40 border border-emerald-200 dark:border-emerald-900/50 flex items-center justify-center mb-4">
            <CheckCircle2 className="w-7 h-7 text-emerald-700 dark:text-emerald-300" />
          </div>
          <h1 className="text-xl sm:text-2xl font-black tracking-tight mb-2">Demande envoyée</h1>
          <p className="text-sm text-foreground/80 leading-relaxed mb-4">
            Si un compte existe avec <strong className="break-all">{email}</strong>,
            l'administration a été notifiée.
          </p>

          <div className="rounded-2xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900/50 p-3 text-left mb-5">
            <div className="flex items-start gap-2.5">
              <Clock className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-700 dark:text-amber-300" />
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-amber-700 dark:text-amber-300 mb-0.5">
                  Délai de réponse
                </div>
                <p className="text-xs text-foreground/85 leading-relaxed">
                  Vous serez contacté sous <strong>24 h ouvrées</strong> par l'administration
                  qui vous communiquera un mot de passe temporaire.
                </p>
              </div>
            </div>
          </div>

          <Button asChild variant="outline" className="w-full rounded-full" data-testid="forgot-back-login-btn">
            <Link to="/login"><ArrowLeft className="w-4 h-4 mr-1.5" /> Retour à la connexion</Link>
          </Button>
        </div>
      </div>
    );
  }

  // --- Formulaire ---
  return (
    <div className="flex items-center justify-center py-8" data-testid="forgot-password-page">
      <div className="mc-card p-6 sm:p-8 w-full max-w-md">
        <Link
          to="/login"
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mb-4 transition-colors"
          data-testid="forgot-back-login"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Retour à la connexion
        </Link>

        <div className="flex items-center gap-3 mb-4">
          <img
            src="/logo-c2r.png"
            alt="CODE 2 ROUES"
            className={`w-11 h-11 rounded-2xl object-cover bg-[#0b1b3a]`}
          />
          <div>
            <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-[hsl(var(--primary))]">
              <KeyRound className="w-3.5 h-3.5" /> Mot de passe oublié
            </div>
            <h1 className="text-lg font-black tracking-tight">Demander une réinitialisation</h1>
          </div>
        </div>

        <div className="rounded-2xl bg-[hsl(var(--primary-soft))] border border-[hsl(var(--primary))]/20 p-3 mb-4">
          <div className="flex items-start gap-2.5">
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-[hsl(var(--primary))]" />
            <div>
              <p className="text-xs text-foreground/85 leading-relaxed">
                Saisissez l'email de votre compte. L'administration sera notifiée et vous
                contactera sous 24 h avec un mot de passe temporaire.
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={onSubmit} className="space-y-3">
          <div>
            <Label htmlFor="email" className="flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5" /> Email du compte *
            </Label>
            <Input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="vous@example.com"
              data-testid="forgot-email-input"
            />
          </div>

          <div>
            <Label htmlFor="message">Message complémentaire (optionnel)</Label>
            <Textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              placeholder="Ex : Je n'arrive plus à me connecter depuis hier, merci de bien vouloir m'envoyer un nouveau mot de passe."
              data-testid="forgot-message-input"
              maxLength={500}
            />
            <p className="text-[11px] text-muted-foreground mt-1">{message.length}/500</p>
          </div>

          <Button
            type="submit"
            disabled={loading || !email.trim()}
            className="mc-btn-primary w-full h-11"
            data-testid="forgot-submit-btn"
          >
            <Send className="w-4 h-4 mr-1.5" /> {loading ? 'Envoi...' : 'Envoyer la demande'}
          </Button>
        </form>
      </div>
    </div>
  );
}
